"use client";

import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import ColorPicker from "@/components/ui/color-picker";
import UploadFile, { FileType } from "@/components/utils/uploadFile";
import { FontSelector } from "@/features/brandKits/components/fontSelector";
import {
    getDefaultLomdaConfig,
    getDefaultPrimaryButton,
    getDefaultSecondaryButton,
    type LomdaConfig,
    type LomdaConfigButton,
    type LomdaConfigStat,
} from "@yedalabs/schema";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type { GetVideoResponseType } from "../../../hooks/useGetVideo";

interface BrandingTabProps {
    video: GetVideoResponseType["data"];
    setReloadVideoKey: (key: number) => void;
}

function ColorField({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string;
    onChange: (next: string) => void;
}) {
    return (
        <div className="flex flex-col gap-1">
            <Label className="text-sm">{label}</Label>
            <Popover>
                <PopoverTrigger asChild>
                    <div className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer">
                        <span dir="ltr">{value}</span>
                        <div
                            className="h-5 w-5 rounded-sm border"
                            style={{ backgroundColor: value }}
                        />
                    </div>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4" align="start">
                    <ColorPicker
                        mode="nice-colors"
                        initialColor={value}
                        onColorChange={onChange}
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}

export default function BrandingTab({
    video,
    setReloadVideoKey,
}: BrandingTabProps) {
    const { locale } = useLocale();
    const queryClient = useQueryClient();

    const defaultLang = video.playerPresets?.embed?.englishPlayer ? "en" : "he";

    const initialConfig = useMemo<LomdaConfig>(() => {
        const defaults = getDefaultLomdaConfig(defaultLang);
        if (!video.lomdaConfig) return defaults;
        // Legacy rows pre-date primaryButton/secondaryButton; backfill via
        // the shared derivation helpers so the editor never sees undefined.
        const base = {
            backgroundColor: video.lomdaConfig.backgroundColor,
            textColor: video.lomdaConfig.textColor,
        };
        return {
            ...video.lomdaConfig,
            primaryButton:
                video.lomdaConfig.primaryButton ??
                getDefaultPrimaryButton(base),
            secondaryButton:
                video.lomdaConfig.secondaryButton ??
                getDefaultSecondaryButton(base),
        };
    }, [video.lomdaConfig, defaultLang]);

    const [config, setConfig] = useState<LomdaConfig>(initialConfig);
    const [savedConfig, setSavedConfig] =
        useState<LomdaConfig>(initialConfig);
    const [saving, setSaving] = useState(false);

    const hasChanges = useMemo(
        () => JSON.stringify(config) !== JSON.stringify(savedConfig),
        [config, savedConfig]
    );

    const updateField = <K extends keyof LomdaConfig>(
        key: K,
        value: LomdaConfig[K]
    ) => setConfig((prev) => ({ ...prev, [key]: value }));

    const updateStartScreen = (patch: Partial<LomdaConfig["startScreen"]>) =>
        setConfig((prev) => ({
            ...prev,
            startScreen: { ...prev.startScreen, ...patch },
        }));

    const updateStat = (index: number, patch: Partial<LomdaConfigStat>) =>
        updateStartScreen({
            stats: config.startScreen.stats.map((s, i) =>
                i === index ? { ...s, ...patch } : s
            ),
        });

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await client.api.storage.video[":videoId"][
                "lomda-config"
            ].$patch({
                param: { videoId: video.id },
                json: config,
            });

            if (!res.ok) {
                toast.error(t("toast.error.update-video"), {
                    keyName: "toast.error.update-video",
                });
                return;
            }

            // Snapshot the just-persisted config so `hasChanges` flips back
            // to false immediately, without waiting for the query refetch.
            setSavedConfig(config);
            toast.success(t("toast.success.changes-saved"), {
                keyName: "toast.success.changes-saved",
            });
            queryClient.invalidateQueries({
                queryKey: ["get-video", video.id],
            });
            setReloadVideoKey(Date.now());
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-4 flex flex-col w-full gap-6">
            <div>
                <Label className="">
                    {t("video-sheet.branding.title", locale)}
                </Label>
                <p className="text-sm text-muted-foreground">
                    {t("video-sheet.branding.subtitle", locale)}
                </p>
            </div>

            {/* Texts */}
            <div className="grid grid-cols-1 gap-3">
                <div className="flex flex-col gap-1">
                    <Label className="text-sm">
                        {t("video-sheet.branding.title.label", locale)}
                    </Label>
                    <Input
                        value={config.startScreen.title}
                        onChange={(e) =>
                            updateStartScreen({ title: e.target.value })
                        }
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <Label className="text-sm">
                        {t("video-sheet.branding.subtitle.label", locale)}
                    </Label>
                    <Input
                        value={config.startScreen.subtitle}
                        onChange={(e) =>
                            updateStartScreen({ subtitle: e.target.value })
                        }
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <Label className="text-sm">
                        {t("video-sheet.branding.button-text.label", locale)}
                    </Label>
                    <Input
                        value={config.startScreen.buttonText}
                        onChange={(e) =>
                            updateStartScreen({ buttonText: e.target.value })
                        }
                    />
                </div>
            </div>

            {/* Stats */}
            <div className="flex flex-col gap-2">
                <Label className="text-sm">
                    {t("video-sheet.branding.stats.title", locale)}
                </Label>
                {config.startScreen.stats.map((stat, i) => (
                    <div key={i} className="grid grid-cols-2 gap-2">
                        <Input
                            placeholder={t(
                                "video-sheet.branding.stats.value-label",
                                locale
                            )}
                            value={stat.label}
                            onChange={(e) =>
                                updateStat(i, { label: e.target.value })
                            }
                        />
                        <Input
                            placeholder={t(
                                "video-sheet.branding.stats.value",
                                locale
                            )}
                            value={stat.value}
                            onChange={(e) =>
                                updateStat(i, { value: e.target.value })
                            }
                        />
                    </div>
                ))}
            </div>

            {/* Hero image */}
            <div className="flex flex-col gap-1">
                <Label className="text-sm">
                    {t("video-sheet.branding.image.label", locale)}
                </Label>
                <UploadFile
                    options={{
                        allowedFileTypes: [FileType.Images],
                        fileUploadPath: `videos/${video.id}/start-screen`,
                        overrideFileName: "hero",
                        maxFileSize: 8,
                        className: "bg-white h-48 mt-1",
                        showImagePreview: true,
                        purgeCache: true,
                    }}
                    initialFile={
                        config.startScreen.image
                            ? {
                                  fileName: "hero",
                                  fileUrl: config.startScreen.image,
                              }
                            : undefined
                    }
                    onUploadFinished={(image) =>
                        updateStartScreen({ image: image.fileUrl })
                    }
                />
            </div>

            {/* Colors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ColorField
                    label={t(
                        "video-sheet.branding.background-color.label",
                        locale
                    )}
                    value={config.backgroundColor}
                    onChange={(c) => updateField("backgroundColor", c)}
                />
                <ColorField
                    label={t("video-sheet.branding.text-color.label", locale)}
                    value={config.textColor}
                    onChange={(c) => updateField("textColor", c)}
                />
            </div>

            {/* Buttons — one row per button (bg / text / border) */}
            <ButtonRow
                label={t("video-sheet.branding.primary-button.label", locale)}
                button={config.primaryButton}
                defaults={getDefaultPrimaryButton({
                    backgroundColor: config.backgroundColor,
                    textColor: config.textColor,
                })}
                onChange={(b) => updateField("primaryButton", b)}
            />
            <ButtonRow
                label={t("video-sheet.branding.secondary-button.label", locale)}
                button={config.secondaryButton}
                defaults={getDefaultSecondaryButton({
                    backgroundColor: config.backgroundColor,
                    textColor: config.textColor,
                })}
                onChange={(b) => updateField("secondaryButton", b)}
            />

            {/* Fonts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FontSelector
                    label={t("video-sheet.branding.title-font.label", locale)}
                    value={{
                        family: config.titleFont.family,
                        url: config.titleFont.url ?? null,
                    }}
                    onChange={(next) =>
                        updateField("titleFont", {
                            family: next.family,
                            url: next.url ?? undefined,
                        })
                    }
                    customFonts={[]}
                    onAddCustomFont={() => {}}
                />
                <FontSelector
                    label={t("video-sheet.branding.body-font.label", locale)}
                    value={{
                        family: config.bodyFont.family,
                        url: config.bodyFont.url ?? null,
                    }}
                    onChange={(next) =>
                        updateField("bodyFont", {
                            family: next.family,
                            url: next.url ?? undefined,
                        })
                    }
                    customFonts={[]}
                    onAddCustomFont={() => {}}
                />
            </div>

            <div className="flex justify-start">
                <Button onClick={handleSave} disabled={saving || !hasChanges}>
                    {saving && <Loader2 className="me-2 size-4 animate-spin" />}
                    {t("video-sheet.branding.save", locale)}
                </Button>
            </div>
        </div>
    );
}

function ButtonRow({
    label,
    button,
    defaults,
    onChange,
}: {
    label: string;
    button: LomdaConfigButton;
    defaults: LomdaConfigButton;
    onChange: (next: LomdaConfigButton) => void;
}) {
    const { locale } = useLocale();
    const isAtDefaults =
        button.backgroundColor === defaults.backgroundColor &&
        button.textColor === defaults.textColor &&
        button.borderColor === defaults.borderColor;
    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
                <Label className="text-sm">{label}</Label>
                {!isAtDefaults && (
                    <Button
                        type="button"
                        variant="link"
                        size="xs"
                        className="text-xs font-normal h-auto py-0.5"
                        onClick={() => onChange(defaults)}
                    >
                        {t(
                            "video-sheet.branding.button.reset-defaults",
                            locale
                        )}
                    </Button>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <ButtonColorCell
                    label={t("video-sheet.branding.button.bg", locale)}
                    value={button.backgroundColor}
                    onChange={(c) =>
                        c !== null &&
                        onChange({ ...button, backgroundColor: c })
                    }
                    allowTransparent
                />
                <ButtonColorCell
                    label={t("video-sheet.branding.button.text", locale)}
                    value={button.textColor}
                    onChange={(c) =>
                        c !== null && onChange({ ...button, textColor: c })
                    }
                />
                <ButtonColorCell
                    label={t("video-sheet.branding.button.border", locale)}
                    value={button.borderColor}
                    onChange={(c) => onChange({ ...button, borderColor: c })}
                    nullable
                />
            </div>
        </div>
    );
}

function ButtonColorCell({
    label,
    value,
    onChange,
    nullable,
    allowTransparent,
}: {
    label: string;
    value: string | null;
    onChange: (next: string | null) => void;
    nullable?: boolean;
    allowTransparent?: boolean;
}) {
    const { locale } = useLocale();
    const isTransparent = value === "transparent";

    return (
        <Popover>
            <PopoverTrigger asChild>
                <div className="flex h-10 items-center rounded-md border border-input bg-background text-xs cursor-pointer overflow-hidden">
                    <div className="shrink-0 flex justify-center items-center bg-muted h-full border-e border-input px-2.5">
                        {label}:
                    </div>
                    <div className="flex w-full text-start">
                        <span dir="ltr" className="truncate px-2">
                            {value ?? "—"}
                        </span>
                    </div>
                    <div
                        className="h-5 w-5 rounded-sm border shrink-0 me-2"
                        style={{
                            backgroundColor: isTransparent
                                ? "transparent"
                                : (value ?? "transparent"),
                            backgroundImage:
                                isTransparent || value === null
                                    ? "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)"
                                    : undefined,
                            backgroundSize:
                                isTransparent || value === null
                                    ? "8px 8px"
                                    : undefined,
                            backgroundPosition:
                                isTransparent || value === null
                                    ? "0 0, 0 4px, 4px -4px, -4px 0px"
                                    : undefined,
                        }}
                    />
                </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3 flex flex-col" align="start">
                <ColorPicker
                    mode="nice-colors"
                    initialColor={
                        value && value !== "transparent" ? value : "#000000"
                    }
                    onColorChange={(c) => onChange(c)}
                />
                <div className="flex w-full justify-center">
                    {allowTransparent && !isTransparent && (
                        <Button
                            className="mt-2.5 font-normal text-xs"
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={() => onChange("transparent")}
                        >
                            {t(
                                "video-sheet.branding.button.transparent",
                                locale
                            )}
                        </Button>
                    )}
                    {nullable && value !== null && (
                        <Button
                            className="mt-2.5 font-normal text-xs"
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={() => onChange(null)}
                        >
                            {t("video-sheet.branding.button.no-border", locale)}
                        </Button>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
